/**
 * Dashboard — the landing page, and the most-seen screen in the product.
 *
 * ── What changed and why ─────────────────────────────────────────────────────────────────────────
 *
 * The page used to be a "bento grid" of hand-rolled MUI `Box`es: each tile declared its own
 * `borderRadius` / `border` / `backgroundImage`, its own hover glow, its own MUI `Skeleton`, and its
 * own centred "no data" paragraph. Four problems came with that. The panels drifted from the rest of
 * the product because nothing shared a definition; the loading and empty states were invented per
 * tile instead of being the two the design system already ships; the chart colours were hex
 * literals, so every series kept its light-mode value after a theme switch; and the accent was
 * baked into `--gradient-primary`, which is not the Cortex accent.
 *
 * So the frame is now the standard one — `ShellHeader` for the title (which is why there is no
 * `<Typography variant="h3">Dashboard</Typography>` here any more; it would render the title twice),
 * `PageContainer` for the body, `FormSection` per block, `DescriptionList` for label/value pairs,
 * `SimpleTable` for the agent table, `LoadingSkeleton` / `EmptyState` / `InlineErrorPanel` for the
 * three states — and the stat tiles are the one bespoke thing left, styled from tokens in
 * DashboardPage.scss.
 *
 * ── What deliberately did not change ─────────────────────────────────────────────────────────────
 *
 * recharts stays: `@coordio/ui` has no charting layer. Its colours come from {@link useChartTokens},
 * which resolves `--color-data-*` / `--color-success|warning|error` against `<html>` at render time
 * and re-reads on a theme switch — see that hook for why the values are not declared in TS.
 *
 * All four React Query fetches, the LLM-metrics/budget effect and the `RealTimeMetrics` SignalR
 * widget are untouched; only their presentation moved.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Button, ShellHeader } from '@coordio/ui';
import {
  DescriptionList,
  EmptyState,
  FormSection,
  InlineErrorPanel,
  LoadingSkeleton,
  PageContainer,
  SimpleTable,
} from '@coordio/ui/layout';
import {
  ArrowRight,
  BarChart3,
  Bot,
  DollarSign,
  MessageSquare,
  Settings,
  SlidersHorizontal,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { BudgetUsageCard } from '../components/dashboard/BudgetUsageCard';
import { CostByModelChart } from '../components/dashboard/CostByModelChart';
import { LlmMetricsCharts } from '../components/dashboard/LlmMetricsCharts';
import { RealTimeMetrics } from '../components/dashboard/RealTimeMetrics';
import { toChartPoints, UsageTrendsChart } from '../components/dashboard/UsageTrendsChart';
import { dashboardService } from '../services/dashboardService';
import { useContextStore } from '../store/contextStore';
import { ROUTES } from '../utils/routes';

import type { AgentPerformanceDto } from '../types/dashboard';
import type { BudgetUsageDto, LlmCallMetricsDto } from '../types/llmMetrics';

import './DashboardPage.scss';

/** How many agents the performance table shows before it stops being a summary. */
const AGENT_ROW_LIMIT = 8;

/** How many models the cost pie splits into before the tail stops being readable. */
const COST_SLICE_LIMIT = 6;

const formatNumber = (value: number) => new Intl.NumberFormat('en-US').format(value);

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }).format(value);

/**
 * Headline figure. Bespoke rather than a `DescriptionList` because these four are the page's
 * primary read and want tile weight; everything about the tile comes from tokens.
 */
const StatTile = ({
  icon,
  label,
  value,
  loading,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  loading: boolean;
  testId: string;
}) => (
  <div className="dashboard__stat" data-testid={testId}>
    <span className="dashboard__stat-icon" aria-hidden>
      {icon}
    </span>
    <span className="dashboard__stat-label">{label}</span>
    {loading ? (
      <LoadingSkeleton rows={1} className="dashboard__stat-skeleton" />
    ) : (
      <span className="dashboard__stat-value">{value}</span>
    )}
  </div>
);

/** Clickable navigation card for the Quick actions block. */
const QuickAction = ({
  icon,
  title,
  description,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}) => (
  <button type="button" className="dashboard__action" onClick={onClick}>
    <span className="dashboard__action-icon" aria-hidden>
      {icon}
    </span>
    <span className="dashboard__action-text">
      <span className="dashboard__action-title">{title}</span>
      <span className="dashboard__action-description">{description}</span>
    </span>
    <ArrowRight className="dashboard__action-arrow" aria-hidden />
  </button>
);

const AGENT_COLUMNS = [
  { id: 'agent', header: 'Agent', cell: (row: AgentPerformanceDto) => row.agentName },
  { id: 'executions', header: 'Executions', cell: (row: AgentPerformanceDto) => formatNumber(row.executionCount) },
  { id: 'success-rate', header: 'Success rate', cell: (row: AgentPerformanceDto) => `${Math.round(row.successRate * 100)}%` },
  {
    id: 'latency',
    header: 'Avg latency',
    cell: (row: AgentPerformanceDto) => `${formatNumber(Math.round(row.averageLatencyMs))}ms`,
  },
  { id: 'cost', header: 'Total cost', cell: (row: AgentPerformanceDto) => formatCurrency(row.totalCost) },
];

export const DashboardPage = () => {
  const navigate = useNavigate();

  const [dateRangeDays] = useState(30);
  const { currentTenant, currentProject } = useContextStore();
  const [llmMetrics, setLlmMetrics] = useState<LlmCallMetricsDto | null>(null);
  const [budgetUsage, setBudgetUsage] = useState<BudgetUsageDto | null>(null);
  const [llmLoading, setLlmLoading] = useState(false);
  const [budgetLoading, setBudgetLoading] = useState(false);

  const { startDateUtc, endDateUtc } = useMemo(() => {
    const end = new Date();
    const start = new Date(Date.now() - dateRangeDays * 24 * 60 * 60 * 1000);
    const toYmd = (d: Date) => d.toISOString().slice(0, 10);
    return { startDateUtc: toYmd(start), endDateUtc: toYmd(end) };
  }, [dateRangeDays]);

  useEffect(() => {
    if (!currentTenant?.id) return;
    setLlmLoading(true);
    dashboardService.getLlmMetrics(currentTenant.id, currentProject?.id || undefined)
      .then(setLlmMetrics)
      .catch(console.error)
      .finally(() => setLlmLoading(false));

    if (currentProject?.id) {
      setBudgetLoading(true);
      dashboardService.getBudgetUsage(currentTenant.id, currentProject.id)
        .then(setBudgetUsage)
        .catch(console.error)
        .finally(() => setBudgetLoading(false));
    }
  }, [currentTenant?.id, currentProject?.id]);

  const hasTenant = !!currentTenant?.id;

  const { data: mainMetrics, isLoading: mainLoading, error: mainError } = useQuery({
    queryKey: ['dashboard', 'mainMetrics', currentTenant?.id, currentProject?.id],
    queryFn: () => dashboardService.getMainMetrics(),
    enabled: hasTenant,
  });

  const { data: usageTrends, isLoading: trendsLoading, error: trendsError } = useQuery({
    queryKey: ['dashboard', 'usageTrends', currentTenant?.id, currentProject?.id, startDateUtc, endDateUtc],
    queryFn: () => dashboardService.getUsageTrends(startDateUtc, endDateUtc),
    enabled: hasTenant,
  });

  const { data: costBreakdown, isLoading: costLoading, error: costError } = useQuery({
    queryKey: ['dashboard', 'costBreakdown', currentTenant?.id, currentProject?.id, startDateUtc, endDateUtc],
    queryFn: () => dashboardService.getCostBreakdown(startDateUtc, endDateUtc),
    enabled: hasTenant,
  });

  const { data: agentPerformance, isLoading: perfLoading, error: perfError } = useQuery({
    queryKey: ['dashboard', 'agentPerformance', currentTenant?.id, currentProject?.id, startDateUtc, endDateUtc],
    queryFn: () => dashboardService.getAgentPerformance(startDateUtc, endDateUtc),
    enabled: hasTenant,
  });

  const usageTrendChartData = toChartPoints(usageTrends ?? []);

  const costByModelData = (costBreakdown?.byModel ?? [])
    .filter((x) => x.cost > 0)
    .slice(0, COST_SLICE_LIMIT)
    .map((x) => ({ name: x.name, value: x.cost }));

  const topAgents = (agentPerformance ?? [])
    .slice()
    .sort((a, b) => (b.totalCost ?? 0) - (a.totalCost ?? 0))
    .slice(0, AGENT_ROW_LIMIT);

  const hasError = Boolean(mainError || trendsError || costError || perfError);

  // Dashboard always renders the full layout — stat tiles, charts, quick actions.
  // When no tenant is selected the queries are disabled, so sections show their
  // empty states (which is honest) rather than hiding the entire dashboard behind
  // a single "No workspace selected" wall.

  return (
    <div className="dashboard" data-testid="dashboard-detail-page">
      <ShellHeader
        title="Dashboard"
        description={currentTenant ? `Activity across ${currentTenant.name} over the last ${dateRangeDays} days.` : 'Your system overview across agents, sessions and spend.'}
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => navigate(ROUTES.DASHBOARD_CUSTOMIZE)}
            data-testid="dashboard-link-customize"
          >
            <SlidersHorizontal className="h-4 w-4 shrink-0" aria-hidden />
            Customize
          </Button>
        }
      />

      <PageContainer className="dashboard__body">
        {hasError && (
          <InlineErrorPanel
            title="Unable to load the dashboard"
            message="Some dashboard data could not be loaded. Check that the API is running and that a workspace and project are selected."
            data-testid="dashboard-detail-error"
          />
        )}

        <div className="dashboard__stats">
          <StatTile
            testId="stat-active-agents"
            icon={<Bot aria-hidden />}
            label="Active agents"
            value={mainMetrics ? formatNumber(mainMetrics.activeAgents) : '—'}
            loading={mainLoading}
          />
          <StatTile
            testId="stat-active-sessions"
            icon={<MessageSquare aria-hidden />}
            label="Active sessions"
            value={mainMetrics ? formatNumber(mainMetrics.activeSessions) : '—'}
            loading={mainLoading}
          />
          <StatTile
            testId="stat-executions-today"
            icon={<Zap aria-hidden />}
            label="Executions today"
            value={mainMetrics ? formatNumber(mainMetrics.totalExecutionsToday) : '—'}
            loading={mainLoading}
          />
          <StatTile
            testId="stat-cost-today"
            icon={<DollarSign aria-hidden />}
            label="Cost today"
            value={mainMetrics ? formatCurrency(mainMetrics.totalCostToday) : '—'}
            loading={mainLoading}
          />
        </div>

        <BudgetUsageCard data={budgetUsage} loading={budgetLoading} />

        <div className="dashboard__split dashboard__split--wide-first">
          <FormSection title="Usage trends" description="Tokens and cost per day over the selected range.">
            <div className="dashboard__plot">
              <UsageTrendsChart data={usageTrendChartData} loading={trendsLoading} rangeDays={dateRangeDays} />
            </div>
          </FormSection>

          <RealTimeMetrics />
        </div>

        <div className="dashboard__split">
          <FormSection title="Cost by model" description={`Top ${COST_SLICE_LIMIT} models by spend.`}>
            <div className="dashboard__plot dashboard__plot--pie">
              <CostByModelChart data={costByModelData} loading={costLoading} rangeDays={dateRangeDays} topN={COST_SLICE_LIMIT} />
            </div>
          </FormSection>

          <FormSection title="Today" description="Counters for the current day, as of the last snapshot.">
            {mainLoading ? (
              <LoadingSkeleton rows={4} />
            ) : (
              <DescriptionList
                items={[
                  { key: 'tokens', label: 'Tokens', value: mainMetrics ? formatNumber(mainMetrics.totalTokensToday) : '—' },
                  { key: 'api-calls', label: 'API calls', value: mainMetrics ? formatNumber(mainMetrics.totalApiCallsToday) : '—' },
                  {
                    key: 'storage',
                    label: 'Storage',
                    value: mainMetrics ? `${formatNumber(mainMetrics.storageUsedBytes)} bytes` : '—',
                  },
                  {
                    key: 'updated',
                    label: 'Updated',
                    value: mainMetrics ? new Date(mainMetrics.asOfUtc).toLocaleTimeString() : '—',
                  },
                ]}
              />
            )}
          </FormSection>
        </div>

        <LlmMetricsCharts data={llmMetrics} loading={llmLoading} />

        <FormSection title="Agent performance" description={`The ${AGENT_ROW_LIMIT} costliest agents in the range.`}>
          {perfLoading ? (
            <LoadingSkeleton rows={4} />
          ) : topAgents.length === 0 ? (
            <EmptyState
              title="No agent activity"
              description="Executions, success rate and latency appear here once agents run in this range."
            />
          ) : (
            <div className="dashboard__table">
              <SimpleTable
                caption="Agent performance for the selected range"
                columns={AGENT_COLUMNS}
                rows={topAgents}
                getRowKey={(row) => row.agentId}
              />
            </div>
          )}
        </FormSection>

        <FormSection title="Quick actions" description="Jump to the areas most often used from here.">
          <div className="dashboard__actions">
            <QuickAction
              icon={<Users aria-hidden />}
              title="Manage agents"
              description="Create and configure AI agents"
              onClick={() => navigate(ROUTES.AGENTS)}
            />
            <QuickAction
              icon={<TrendingUp aria-hidden />}
              title="View sessions"
              description="Monitor active sessions"
              onClick={() => navigate(ROUTES.SESSIONS)}
            />
            <QuickAction
              icon={<Settings aria-hidden />}
              title="Configure tools"
              description="Manage tools and integrations"
              onClick={() => navigate(ROUTES.TOOLS)}
            />
            <QuickAction
              icon={<SlidersHorizontal aria-hidden />}
              title="Customize dashboard"
              description="Arrange widgets and views"
              onClick={() => navigate(ROUTES.DASHBOARD_CUSTOMIZE)}
            />
            <QuickAction
              icon={<BarChart3 aria-hidden />}
              title="View reports"
              description="Access analytics and reports"
              onClick={() => navigate(ROUTES.USAGE_ANALYTICS)}
            />
          </div>
        </FormSection>
      </PageContainer>
    </div>
  );
};
